'use client';

import { ChevronRight } from 'lucide-react';
import Link from 'next/link';
import { useTranslations } from 'next-intl';
import { EmptyState } from '@/components/shared/EmptyState';
import { GameCard } from '@/components/shared/m-game-card';
import { useGameCategories } from '@/features/game-categories/hooks/useGameCategories';
import { useGames } from '@/features/games/hooks/useGames';

interface FavoriteGameProps {
  categoryName?: string;
}

export const FavoriteGame = ({ categoryName }: FavoriteGameProps) => {
  const t = useTranslations();
  const { data: categories = [], isLoading: categoriesLoading } =
    useGameCategories();
  const categoryId = categories.find(c => c.name === categoryName)?.id;
  const { data, isLoading, isError, refetch } = useGames({
    categoryId,
    page: 1,
    limit: 3,
    favorite: true,
    enabled: !categoriesLoading && (!categoryName || Boolean(categoryId)),
  });
  const favorites = data?.games ?? [];

  if (isLoading && !data) {
    return (
      <div className="py-2">
        <h4 className="font-bold text-sm text-[#FFA035] px-4 pb-2">
          Favorite Games
        </h4>
        <div className="p-2 grid grid-cols-3 gap-2 bg-[#801C80]/40 rounded-lg mx-2 animate-pulse">
          {Array.from({ length: 3 }).map((_, i) => (
            <div
              // biome-ignore lint/suspicious/noArrayIndexKey: skeleton loading cards
              key={`fav-skeleton-${i}`}
              className="h-[120px] rounded-lg bg-white/5"
            />
          ))}
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div role="alert" className="py-6 text-center">
        <EmptyState message={t('ui.games.load_failed')} />
        <button type="button" onClick={() => refetch()}>
          {t('ui.games.retry')}
        </button>
      </div>
    );
  }

  if (favorites.length === 0) {
    return null;
  }

  return (
    <div>
      <div className="flex justify-between items-center">
        <h4 className="font-bold text-sm text-[#FFA035] px-4 pb-2">
          Favorite Games
        </h4>
        <Link
          href={`/pages/m-more-games?type=favorite${categoryId ? `&category=${encodeURIComponent(categoryId)}` : ''}`}
          className="text-[10px] text-white flex items-center gap-1 cursor-pointer hover:opacity-80 active:opacity-70 px-4"
        >
          more games <ChevronRight className="size-3" />
        </Link>
      </div>
      <div className="p-2 grid grid-cols-3 gap-2 bg-[#801C80]/40 rounded-lg mx-2">
        {favorites.map(game => (
          <GameCard
            key={game.id}
            gameId={game.id}
            imageUrl={game.image ?? undefined}
            title={game.name}
            liked={game.isFavorite}
          />
        ))}
      </div>
    </div>
  );
};
